(() => {
'use strict';

const STORAGE_KEY = 'tanatel-smart-recommendation-history-v2';
const LEGACY_KEY = 'minimog-recently-viewed';
const MAX_HISTORY = 12;
const MAX_AGE = 30 * 24 * 60 * 60 * 1000;
const TANATEL_RECOMMENDATIONS_VERSION = '4.1.0-family-smart-safe-fallback';

const CROSS_SELL_RULES = [
  { trigger: 'غرف نوم كاملة', targets: ['مرتبة', 'دولاب', 'Lamp', 'اريكة', 'سجادة'] },
  { trigger: 'اطقم الكنب', targets: ['سجادة', 'نباتات', 'كونسول', 'لوحات', 'كتاب ديكور', 'طاولات قهوة'] },
  { trigger: 'اطقم طاولات الطعام', targets: ['طاولات طعام فردية', 'كراسي طعام فردية', 'بوفيه', 'نباتات', 'لوحات'] }
];

const normalize = (value) => String(value || '').trim().toLowerCase();
const splitTokens = (value) => Array.from(new Set(
  String(value || '').split('|').map(normalize).filter(Boolean)
));

const intersectionCount = (left, right, cap = Infinity) => {
  if (!left.length || !right.length) return 0;
  const rightSet = new Set(right);
  let count = 0;
  for (const value of left) {
    if (rightSet.has(value) && ++count >= cap) break;
  }
  return count;
};

const safeRead = (key, fallback) => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null');
    return value ?? fallback;
  } catch (_) {
    return fallback;
  }
};

const safeWrite = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (_) {}
};

const readHistory = () => {
  const now = Date.now();
  const stored = safeRead(STORAGE_KEY, []);
  if (!Array.isArray(stored)) return [];
  return stored
    .filter((entry) => entry && entry.id && now - Number(entry.at || 0) <= MAX_AGE)
    .slice(0, MAX_HISTORY);
};

const readLegacyIds = () => {
  const ids = safeRead(LEGACY_KEY, []);
  return Array.isArray(ids) ? new Set(ids.map(String)) : new Set();
};

const contextFromElement = (element) => ({
  id: String(element.dataset.currentProductId || ''),
  type: normalize(element.dataset.currentProductType),
  vendor: normalize(element.dataset.currentProductVendor),
  tags: splitTokens(element.dataset.currentProductTags),
  collections: splitTokens(element.dataset.currentProductCollections),
  at: Date.now()
});

const candidateFromElement = (element, index) => ({
  element,
  index,
  id: String(element.dataset.productId || ''),
  type: normalize(element.dataset.productType),
  vendor: normalize(element.dataset.productVendor),
  tags: splitTokens(element.dataset.productTags),
  collections: splitTokens(element.dataset.productCollections),
  target: normalize(element.dataset.tanatelCrossSellTarget),
  familyKind: normalize(element.dataset.tanatelFamilyCandidate),
  available: element.dataset.productAvailable !== 'false'
});

const recordCurrentProduct = (context) => {
  if (!context.id) return;
  const previous = readHistory().filter((entry) => String(entry.id) !== context.id);
  safeWrite(STORAGE_KEY, [context, ...previous].slice(0, MAX_HISTORY));
};

const scoreCandidate = (candidate, current, history, legacyIds) => {
  let score = candidate.available ? 1000 : 0;

  if (candidate.type && candidate.type === current.type) score += 18;
  if (candidate.vendor && candidate.vendor === current.vendor) score += 2;
  score += intersectionCount(candidate.tags, current.tags, 4) * 5;
  score += intersectionCount(candidate.collections, current.collections, 2) * 10;

  history.forEach((entry, historyIndex) => {
    if (!entry || String(entry.id) === current.id) return;

    const weight = Math.max(0.35, 1 - historyIndex * 0.08);
    const entryType = normalize(entry.type);
    const entryVendor = normalize(entry.vendor);
    const entryTags = Array.isArray(entry.tags) ? entry.tags.map(normalize) : [];
    const entryCollections = Array.isArray(entry.collections) ? entry.collections.map(normalize) : [];

    if (candidate.type && candidate.type === entryType) score += 12 * weight;
    if (candidate.vendor && candidate.vendor === entryVendor) score += 1.5 * weight;
    score += intersectionCount(candidate.tags, entryTags, 3) * 4 * weight;
    score += intersectionCount(candidate.collections, entryCollections, 2) * 6 * weight;
  });

  if (legacyIds.has(candidate.id)) score += 3;

  score += Math.max(0, 100 - candidate.index) / 1000;
  return score;
};

const findCrossSellRule = (currentTags) => {
  if (!Array.isArray(currentTags) || !currentTags.length) return null;

  return CROSS_SELL_RULES.find((rule) =>
    currentTags.includes(normalize(rule.trigger))
  ) || null;
};

const selectCrossSellCandidates = (candidates, rule) => {
  const selected = [];
  const selectedIds = new Set();

  rule.targets.forEach((targetTag) => {
    const target = normalize(targetTag);

    const matches = candidates
      .filter((candidate) =>
        candidate.available &&
        !selectedIds.has(candidate.id) &&
        candidate.tags.includes(target)
      )
      .sort((a, b) => b.score - a.score || a.index - b.index);

    if (!matches.length) return;

    const winner = matches[0];
    selected.push(winner);
    selectedIds.add(winner.id);
  });

  return selected;
};

const selectFamilyCandidates = (candidates, component, displayLimit) => {
  const desiredSlots = Math.max(
    1,
    Math.min(displayLimit, Number(component.dataset.tanatelFamilySlots || displayLimit))
  );

  const selected = [];
  const selectedIds = new Set();
  const primaryTargets = [];
  const fallbackTargets = [];

  candidates.forEach((candidate) => {
    if (!candidate.target) return;

    if (candidate.familyKind === 'fallback') {
      if (!fallbackTargets.includes(candidate.target)) fallbackTargets.push(candidate.target);
      return;
    }

    if (!primaryTargets.includes(candidate.target)) primaryTargets.push(candidate.target);
  });

  const pickBestForTarget = (target, kind) => {
    const matches = candidates
      .filter((candidate) =>
        candidate.available &&
        candidate.target === target &&
        candidate.familyKind === kind &&
        !selectedIds.has(candidate.id)
      )
      .sort((a, b) => b.score - a.score || a.index - b.index);

    if (!matches.length) return false;

    const winner = matches[0];
    selected.push(winner);
    selectedIds.add(winner.id);
    return true;
  };

  for (const target of primaryTargets) {
    if (selected.length >= desiredSlots) break;
    pickBestForTarget(target, 'primary');
  }

  // Fill every missing slot from the approved fallback pool:
  // مرتبة -> نباتات -> لوحات -> اكسسوارات.
  for (const target of fallbackTargets) {
    if (selected.length >= desiredSlots) break;

    const matches = candidates
      .filter((candidate) =>
        candidate.available &&
        candidate.target === target &&
        candidate.familyKind === 'fallback' &&
        !selectedIds.has(candidate.id)
      )
      .sort((a, b) => b.score - a.score || a.index - b.index);

    for (const candidate of matches) {
      if (selected.length >= desiredSlots) break;
      if (selectedIds.has(candidate.id)) continue;
      selected.push(candidate);
      selectedIds.add(candidate.id);
    }
  }

  return selected;
};

const activateNativeSlider = (component, grid, totalProducts) => {
  if (component.dataset.tanatelSliderEnabled !== 'true') return;
  if (component.dataset.tanatelSliderInitialized === 'true') return;

  const container = component.querySelector('.tanatel-recommendations-list');
  if (!container || !grid || totalProducts <= 1) return;

  const SwiperCtor = window.EnfitahLibs && window.EnfitahLibs.Swiper;
  if (typeof SwiperCtor !== 'function') {
    if (component.dataset.tanatelSliderRetry !== 'true') {
      component.dataset.tanatelSliderRetry = 'true';
      window.setTimeout(() => {
        component.dataset.tanatelSliderRetry = 'false';
        activateNativeSlider(component, grid, totalProducts);
      }, 60);
    }
    return;
  }

  const desktopCount = Math.max(1, Number(component.dataset.tanatelSliderDesktop || 4));
  const mobileCount = Math.max(1, Number(component.dataset.tanatelSliderMobile || 2));
  const desktopGap = 30;
  const mobileGap = 12;

  container.classList.add('swiper-container');
  grid.classList.add('swiper-wrapper');
  component.dataset.tanatelSliderReady = 'true';

  const prev = component.querySelector('.m-slider-controls__button-prev');
  const next = component.querySelector('.m-slider-controls__button-next');

  const slider = new SwiperCtor(container, {
    slidesPerView: Math.min(mobileCount, totalProducts),
    spaceBetween: mobileGap,
    loop: false,
    watchOverflow: true,
    observer: false,
    observeParents: false,
    threshold: 4,
    grabCursor: totalProducts > mobileCount,
    allowTouchMove: true,
    speed: 420,
    breakpoints: {
      768: {
        slidesPerView: Math.min(3, totalProducts),
        spaceBetween: 20
      },
      1024: {
        slidesPerView: Math.min(desktopCount, totalProducts),
        spaceBetween: desktopGap
      }
    }
  });

  if (prev) {
    prev.classList.remove('m:hidden');
    prev.addEventListener('click', () => slider.slidePrev(), { passive: true });
  }

  if (next) {
    next.classList.remove('m:hidden');
    next.addEventListener('click', () => slider.slideNext(), { passive: true });
  }

  component.tanatelSlider = slider;
  component.dataset.tanatelSliderInitialized = 'true';
};

const rank = (component) => {
  if (component.dataset.tanatelSmartRanked === 'true') return true;

  const grid = component.querySelector('.tanatel-recommendations-grid');
  if (!grid) return false;

  const nodes = Array.from(grid.querySelectorAll(':scope > [data-tanatel-smart-candidate]'));
  if (!nodes.length) return false;

  const current = contextFromElement(component);
  const history = readHistory();
  const legacyIds = readLegacyIds();
  const displayLimit = Math.max(1, Math.min(10, Number(component.dataset.displayLimit || 8)));

  const unique = [];
  const seen = new Set([current.id]);

  nodes.forEach((node, index) => {
    const candidate = candidateFromElement(node, index);

    if (!candidate.id || seen.has(candidate.id)) {
      node.remove();
      return;
    }

    seen.add(candidate.id);
    candidate.score = scoreCandidate(candidate, current, history, legacyIds);
    unique.push(candidate);
  });

  const directFamilyMode = component.dataset.tanatelDirectCrossSell === 'true';
  const crossSellRule = findCrossSellRule(current.tags);
  let finalCandidates = [];

  if (directFamilyMode) {
    finalCandidates = selectFamilyCandidates(unique, component, displayLimit);

    const selectedIds = new Set(finalCandidates.map((candidate) => candidate.id));
    unique.forEach((candidate) => {
      if (!selectedIds.has(candidate.id)) candidate.element.remove();
    });

    component.dataset.tanatelCrossSellRule = component.dataset.tanatelFamily || 'family';
    component.dataset.tanatelCrossSellCount = String(finalCandidates.length);
  } else if (crossSellRule) {
    finalCandidates = selectCrossSellCandidates(unique, crossSellRule);

    const selectedIds = new Set(finalCandidates.map((candidate) => candidate.id));
    unique.forEach((candidate) => {
      if (!selectedIds.has(candidate.id)) candidate.element.remove();
    });

    component.dataset.tanatelCrossSellRule = crossSellRule.trigger;
    component.dataset.tanatelCrossSellCount = String(finalCandidates.length);
  } else {
    unique.sort((a, b) => b.score - a.score || a.index - b.index);
    finalCandidates = unique.slice(0, displayLimit);

    const keepIds = new Set(finalCandidates.map((candidate) => candidate.id));
    unique.forEach((candidate) => {
      if (!keepIds.has(candidate.id)) candidate.element.remove();
    });
  }

  const fragment = document.createDocumentFragment();

  finalCandidates.forEach((candidate) => {
    candidate.element.removeAttribute('data-tanatel-smart-overflow');
    fragment.appendChild(candidate.element);
  });

  grid.appendChild(fragment);
  component.dataset.tanatelSmartRanked = 'true';
  component.dataset.tanatelRecommendationsVersion = TANATEL_RECOMMENDATIONS_VERSION;

  activateNativeSlider(component, grid, finalCandidates.length);

  return true;
};

const arm = (component) => {
  if (!component || component.dataset.tanatelSmartArmed === 'true') return;
  component.dataset.tanatelSmartArmed = 'true';

  const current = contextFromElement(component);
  recordCurrentProduct(current);

  const tryRank = () => rank(component);
  if (tryRank()) return;

  const observer = new MutationObserver(() => {
    if (tryRank()) observer.disconnect();
  });

  observer.observe(component, { childList: true, subtree: true });
};

const init = () => {
  const components = document.querySelectorAll('[data-tanatel-smart-recommendations]');
  if (!components.length) return;

  if (!('IntersectionObserver' in window)) {
    components.forEach(arm);
    return;
  }

  const viewportObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      viewportObserver.unobserve(entry.target);
      arm(entry.target);
    });
  }, { rootMargin: '0px 0px 800px 0px' });

  components.forEach((component) => viewportObserver.observe(component));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
})();


/* TANATEL V7 — SERVER-SIDE FAMILY RECOMMENDATIONS
   No fetch. No search endpoint. No collection endpoint.
   Family products are already selected and rendered by Liquid.
*/
(() => {
  const VERSION = '7.0.1-server-side-collections-syntax-fix';

  const init = (component) => {
    if (!component || component.dataset.tanatelServerFamilyReady === 'true') return;

    const grid = component.querySelector('[data-tanatel-server-family-grid]');
    if (!grid) return;

    component.dataset.tanatelServerFamilyReady = 'true';
    component.dataset.tanatelRecommendationsVersion = VERSION;

    const total = grid.querySelectorAll('[data-tanatel-server-candidate]').length;

    try {
      if (typeof activateNativeSlider === 'function') {
        activateNativeSlider(component, grid, total);
      }
    } catch (_) {}
  };

  const boot = (root = document) => {
    root.querySelectorAll('[data-tanatel-server-family-recommendations]')
      .forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
